import { useCallback, useSyncExternalStore } from "react"

import {
  ColorSwatch,
  TagOptionCheckbox,
  toast,
  type RowAction,
} from "@coval/ui"
import { Tag } from "lucide-react"

import { getDefaultTagColor } from "../constants/tag-colors"
import { useOrganizationTags } from "../routes/useOrganizationTags"
import { readRecordTags, writeRecordTags } from "../utils/recordTagWrite"
import { buildTagFilterItems } from "../utils/tagCatalog"
import { normalizeTags } from "../utils/tagNormalization"
import {
  getTagWriteSnapshot,
  subscribeToTagWrites,
  tagWriteKey,
} from "../utils/tagWriteLedger"

interface UseTagRowActionParams<T> {
  disabled: boolean
  errorFallback: string
  errorTitle: string
  getId: (record: T) => string
  getTags: (record: T) => readonly string[] | null | undefined
  isTaggable?: (record: T) => boolean
  resource: string
  updateTags: (record: T, tags: string[]) => Promise<unknown>
}

const isAlwaysTaggable = () => true

/** Builds the dedicated Tags submenu shared by table row menus; toggles use the same queue and ledger as cells and bulk actions so consecutive changes cannot replace one another with a stale whole-list write. */
export function useTagRowAction<T>({
  disabled,
  errorFallback,
  errorTitle,
  getId,
  getTags,
  isTaggable = isAlwaysTaggable,
  resource,
  updateTags,
}: UseTagRowActionParams<T>) {
  const { suggestions, invalidate: invalidateOrganizationTags } =
    useOrganizationTags()
  const writtenTagsByKey = useSyncExternalStore(
    subscribeToTagWrites,
    getTagWriteSnapshot,
    getTagWriteSnapshot,
  )

  return useCallback(
    (record: T): RowAction<T> | null => {
      if (!isTaggable(record)) {
        return null
      }

      const recordId = getId(record)
      const currentTags =
        writtenTagsByKey.get(tagWriteKey(resource, recordId)) ??
        normalizeTags(getTags(record) ?? [])
      const selected = new Set(currentTags)
      const items = buildTagFilterItems({
        selectedTags: currentTags,
        suggestions,
      })

      return {
        label: "Tags",
        icon: <Tag />,
        disabled,
        onSelect: () => undefined,
        separatorBefore: true,
        searchPlaceholder: "Search tags...",
        items:
          items.length === 0
            ? [{ label: "No tags", disabled: true, onSelect: () => undefined }]
            : items.map((item) => ({
                label: item.tag,
                disabled,
                icon: (
                  <span
                    className="flex shrink-0 items-center gap-2"
                    aria-hidden="true"
                  >
                    <TagOptionCheckbox checked={selected.has(item.tag)} />
                    <ColorSwatch
                      color={item.color || getDefaultTagColor(item.tag)}
                      className="size-2.5"
                    />
                  </span>
                ),
                keepOpen: true,
                onSelect: () => {
                  if (disabled) {
                    return
                  }

                  const latestTags = readRecordTags(
                    resource,
                    recordId,
                    getTags(record),
                  )
                  const nextTags = latestTags.includes(item.tag)
                    ? latestTags.filter((tag) => tag !== item.tag)
                    : normalizeTags([...latestTags, item.tag])

                  void writeRecordTags({
                    recordId,
                    resource,
                    tags: nextTags,
                    updateTags: (tags) => updateTags(record, tags),
                  }).then(({ error, landed, ownsRecord }) => {
                    if (landed) {
                      void invalidateOrganizationTags()
                      return
                    }
                    if (ownsRecord) {
                      toast.error({
                        title: errorTitle,
                        error,
                        fallback: errorFallback,
                      })
                    }
                  })
                },
              })),
      }
    },
    [
      disabled,
      errorFallback,
      errorTitle,
      getId,
      getTags,
      invalidateOrganizationTags,
      isTaggable,
      resource,
      suggestions,
      updateTags,
      writtenTagsByKey,
    ],
  )
}
